import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckSquare, Headphones, CheckCircle2, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CandidateAssessmentOptionsProps {
  assessmentId: string;
  candidateId: string;
  candidateName: string;
  candidateEmail: string;
  candidatePhone?: string;
  assessmentDetails?: {
    title: string;
    skills: string[];
    company_name: string;
    custom_questions?: string[];
  };
}

interface AssessmentDetails {
  title?: string;
  skills?: string[];
  company_name?: string;
  custom_questions?: string[];
  voice_id?: string;
  language?: string;
  accent?: string;
  skill_questions?: string[] | Record<string, string[]>;
  assessment_type?: string;
}

// Language code mapping from language_accents.csv (copied from AssessmentInterface.tsx)
const languageCodeMap: Record<string, string> = {
  'Arabic': 'ar',
  'Chinese': 'zh',
  'English': 'en',
  'French': 'fr',
  'German': 'de',
};

const CandidateAssessmentOptions: React.FC<CandidateAssessmentOptionsProps> = ({
  assessmentId,
  candidateId,
  candidateName,
  candidateEmail,
  candidatePhone,
  assessmentDetails: initialAssessmentDetails,
}) => {
  const [startingAssessment, setStartingAssessment] = useState(false);
  const firstName = candidateName.split(' ')[0];
  const [assessmentDetails, setAssessmentDetails] = useState<AssessmentDetails>(
    initialAssessmentDetails || {
      title: '',
      skills: [],
      company_name: '',
      custom_questions: [],
      voice_id: '',
      language: '',
      accent: '',
      skill_questions: {},
      assessment_type: ''
    }
  );

  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      // Only fetch if we don't have the details already
      if (!initialAssessmentDetails) {
        try {
          console.log('[ASSESSMENT DETAILS] Fetching assessment details:', {
            assessmentId,
            timestamp: new Date().toISOString()
          });

          // First get the assessment
          const { data: assessment, error: assessmentError } = await supabase
            .from('assessments')
            .select(`
              title,
              skills,
              organization_id,
              custom_questions,
              voice_id,
              language,
              accent,
              skill_questions,
              assessment_type
            `)
            .eq('id', assessmentId)
            .single();

          if (assessmentError) {
            console.error('[ASSESSMENT DETAILS] Error fetching assessment details:', {
              error: assessmentError,
              assessmentId,
              timestamp: new Date().toISOString()
            });
            throw assessmentError;
          }

          if (!assessment) {
            console.error('[ASSESSMENT DETAILS] No assessment data found:', {
              assessmentId,
              timestamp: new Date().toISOString()
            });
            throw new Error('No assessment data found');
          }

          console.log('[ASSESSMENT DETAILS] Successfully fetched assessment:', {
            assessment,
            timestamp: new Date().toISOString()
          });

          // Then get the organization separately
          let organizationName = 'Unknown Organization';
          if (assessment.organization_id) {
            console.log('[ASSESSMENT DETAILS] Fetching organization:', {
              organizationId: assessment.organization_id,
              timestamp: new Date().toISOString()
            });
            
            const { data: organization, error: orgError } = await supabase
              .from('organizations')
              .select('name')
              .eq('id', assessment.organization_id)
              .single();
              
            if (!orgError && organization && organization.name) {
              organizationName = organization.name;
              console.log('[ASSESSMENT DETAILS] Found organization name:', {
                name: organizationName,
                timestamp: new Date().toISOString()
              });
            } else {
              console.warn('[ASSESSMENT DETAILS] Organization not found:', {
                error: orgError,
                organizationId: assessment.organization_id,
                timestamp: new Date().toISOString()
              });
            }
          } else {
            console.warn('[ASSESSMENT DETAILS] No organization ID for assessment:', {
              assessmentId,
              timestamp: new Date().toISOString()
            });
          }

          setAssessmentDetails({
            title: assessment.title,
            skills: assessment.skills,
            company_name: organizationName,
            custom_questions: assessment.custom_questions || [],
            voice_id: assessment.voice_id,
            language: assessment.language,
            accent: assessment.accent,
            skill_questions: assessment.skill_questions,
            assessment_type: assessment.assessment_type
          });
        } catch (error) {
          console.error('[ASSESSMENT DETAILS] Error fetching assessment details:', {
            error,
            assessmentId,
            timestamp: new Date().toISOString()
          });
          toast.error('Failed to load assessment details. Please contact support.');
        }
      }
    };

    fetchAssessmentDetails();
  }, [assessmentId, initialAssessmentDetails]);

  const handleStartBrowserAssessment = async () => {
    if (!assessmentId || !candidateEmail) {
      toast.error('Missing assessment or candidate information');
      return;
    }

    try {
      setStartingAssessment(true);
      console.log('[START ASSESSMENT] Starting assessment with details:', {
        assessmentId,
        candidateId,
        candidateName,
        candidateEmail,
        timestamp: new Date().toISOString()
      });

      // Check if the candidate has already completed this assessment
      const { data: candidates, error: checkError } = await supabase
        .from('candidates')
        .select('id, status')
        .eq('assessment_id', assessmentId)
        .eq('email', candidateEmail);

      if (!checkError && candidates && candidates.length > 0) {
        const existingCandidate = candidates[0];
        if (existingCandidate.status === 'completed') {
          toast.error(
            'You have already completed this assessment. Each candidate can only take an assessment once.',
            {
              duration: 5000,
              description: 'If you believe this is a mistake, please contact the assessment administrator.'
            }
          );
          setStartingAssessment(false);
          return;
        }
      }

      // Update candidate status to in_progress
      try {
        const { error: updateError } = await supabase
          .from('candidates')
          .update({ status: 'in_progress' })
          .eq('id', candidateId);
        
        if (updateError) {
          console.error('[START ASSESSMENT] Error updating candidate status:', updateError);
        } else {
          console.log('[START ASSESSMENT] Successfully updated candidate status to in_progress');
        }
      } catch (error) {
        console.error('[START ASSESSMENT] Exception updating candidate status:', error);
      }

      // Construct the URL with proper query parameters
      console.log('[START ASSESSMENT] Redirecting to assessment interface...');
      const url = `/assessment/${assessmentId}/interface?candidateId=${encodeURIComponent(candidateId)}&email=${encodeURIComponent(candidateEmail)}&name=${encodeURIComponent(candidateName)}`;
      console.log('[START ASSESSMENT] Redirect URL:', url);

      // Add a small delay to allow the status update to complete
      setTimeout(() => {
        window.location.href = url;
      }, 500);
      
    } catch (error) {
      console.error('[START ASSESSMENT] Error starting browser assessment:', error);
      toast.error('Failed to start assessment. Please contact support.');
      setStartingAssessment(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex justify-center mb-4">
        <div className="p-3 rounded-full bg-roundone-light-purple/20">
          <CheckCircle2 size={28} className="text-roundone-purple" />
        </div>
      </div>
      <h2 className="text-xl font-bold text-center text-roundone-dark-purple mb-2">
        Welcome, {firstName}!
      </h2>
      <p className="text-center text-muted-foreground mb-6">
        You're about to start your assessment.
      </p>
      
      <div className="w-full bg-roundone-light-purple/10 rounded-lg p-4 mb-6 border border-roundone-light-purple/20">
        <h3 className="font-medium text-roundone-dark-purple mb-3 flex items-center">
          <CheckSquare className="h-4 w-4 mr-2 text-roundone-purple" /> Before you start:
        </h3>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start">
            <span className="mr-2 text-roundone-purple">•</span> Ensure you're in a quiet environment
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-roundone-purple">•</span> Test your microphone
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-roundone-purple">•</span> Have your resume handy for reference
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-roundone-purple">•</span> Allow approximately 5-7 minutes to complete
          </li>
        </ul>
      </div>
      
      <div className="mb-4">
        <p className="text-sm text-muted-foreground">
          Start the assessment right now in your browser. You'll need a microphone and about 5-7 minutes of uninterrupted time.
        </p>
      </div>
      
      <Button 
        className="w-full gradient-button py-2.5 mt-2 flex items-center justify-center gap-2 text-white"
        onClick={handleStartBrowserAssessment}
        disabled={startingAssessment}
      >
        {startingAssessment ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-30 border-t-white rounded-full"></div>
            <span>Loading Assessment...</span>
          </>
        ) : (
          <>
            <span>Start Assessment Now</span>
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      <div className="text-center text-xs text-muted-foreground mt-6">
        Powered by <a href="https://roundone.ai" className="text-roundone-purple hover:underline">RoundOne AI</a>
      </div>
    </div>
  );
};

export default CandidateAssessmentOptions;
